import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  SiInstagram, 
  SiFacebook, 
  SiX, 
  SiLinkedin, 
  SiTiktok, 
  SiYoutube, 
} from 'react-icons/si';
import logoWhite from '../../assets/images/logo-white.svg';
import bigSpeedessyLogo from '../../assets/images/big-speedessy-logo.svg';
import { useSmoothScroll } from '../../hooks/useSmoothScroll';

interface FooterLink {
  title: string;
  path: string;
  onClick?: () => void;
}

const LETS_TALK_URL =
  'https://outlook.office365.com/book/LetsTalk@Speedyssey.onmicrosoft.com/?ismsaljsauthenabled=true';

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollToSection } = useSmoothScroll();

  const handleFAQClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        scrollToSection('faq');
      }, 100);
    } else {
      scrollToSection('faq');
    }
  };

  const handleContactClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        scrollToSection('contact');
      }, 100);
    } else {
      scrollToSection('contact');
    }
  };

  const quickLinks: FooterLink[] = [
    { title: 'About', path: '/about' },
    { title: 'Services', path: '/services' },
    { title: 'Contact', path: '#contact', onClick: handleContactClick },
    { title: 'Request a Custom Quote', path: '/request-quote' },
  ];

  const resources: FooterLink[] = [
    { title: 'Blog', path: '/news' },
    { title: 'Help Center', path: '#contact', onClick: handleContactClick },
    { title: 'Privacy Policy', path: '/privacy-policy' },
    { title: 'Term & Conditions', path: '/terms-conditions' },
  ];

  const exploreMore: FooterLink[] = [
    { title: 'Become a Delivery Partner', path: '/deliver' },
    // { title: 'Careers', path: '/careers' },
    { title: 'FAQs', path: '#faq', onClick: handleFAQClick },
  ];

  const socialLinks = [
    { 
      icon: <SiFacebook size={22} />, 
      href: 'https://www.facebook.com/my.speedyssey', 
      label: 'Facebook' 
    },
    { 
      icon: <SiLinkedin size={22} />, 
      href: 'https://www.linkedin.com/company/speedyssey/?viewAsMember=true', 
      label: 'LinkedIn' 
    },
    { 
      icon: <SiInstagram size={22} />, 
      href: 'https://www.instagram.com/speedysseydelivery/', 
      label: 'Instagram' 
    },
    { 
      icon: <SiTiktok size={22} />, 
      href: 'https://www.tiktok.com/@speedyssey?lang=en', 
      label: 'TikTok' 
    },
    { 
      icon: <SiX size={22} />, 
      href: 'https://x.com/speedyssey', 
      label: 'X (Twitter)' 
    },
    { 
      icon: <SiYoutube size={22} />, 
      href: 'https://www.youtube.com/@speedyssey', 
      label: 'YouTube' 
    },
  ];

  return (
    <footer className="bg-[#1A1A1A] text-white pt-20 pb-0 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo and Socials */}
          <div className="flex flex-col">
            <img src={logoWhite} alt="Speedyssey" className="h-12 w-auto mb-4 self-start" />
            <p className="text-sm text-gray-400 mb-6">
              Trusted by businesses worldwide. Powered by people, driven by logistics.
            </p>
            <div className="flex space-x-6 mb-6">
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.label}
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  {link.icon}
                </a>
              ))}
            </div>
            <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} Speedyssey. All right reserved.</p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="special-header text-xl font-bold mb-4">QUICK LINKS</h3>
            <ul className="space-y-6">
              {quickLinks.map((link) => (
                <li key={link.title}>
                  {link.onClick ? (
                    <button
                      onClick={link.onClick}
                      className="text-gray-400 hover:text-white transition-colors duration-300 text-left cursor-pointer"
                    >
                      {link.title}
                    </button>
                  ) : link.path.startsWith('/') ? (
                    <Link
                      to={link.path}
                      className="text-gray-400 hover:text-white transition-colors duration-300"
                    >
                      {link.title}
                    </Link>
                  ) : (
                    <a
                      href={link.path}
                      className="text-gray-400 hover:text-white transition-colors duration-300"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.title}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="special-header text-xl font-bold mb-4">RESOURCES</h3>
            <ul className="space-y-6">
              {resources.map((link) => (
                <li key={link.title}>
                  {link.onClick ? (
                    <button
                      onClick={link.onClick}
                      className="text-gray-400 hover:text-white transition-colors duration-300 text-left"
                    >
                      {link.title}
                    </button>
                  ) : (
                    <a href={link.path} className="text-gray-400 hover:text-white transition-colors duration-300">
                      {link.title}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Explore More */}
          <div>
            <h3 className="special-header text-xl font-bold mb-4">EXPLORE MORE</h3>
            <ul className="space-y-6">
              {exploreMore.map((link) => (
                <li key={link.title}>
                  {link.onClick ? (
                    <button 
                      onClick={link.onClick}
                      className="text-gray-400 hover:text-white transition-colors duration-300 text-left"
                    >
                      {link.title}
                    </button>
                  ) : (
                    <a href={link.path} className="text-gray-400 hover:text-white transition-colors duration-300">
                      {link.title}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Background Text */}
      <div className=" w-full text-center mt-28 sm:px-16 container mx-auto">
        <img src={bigSpeedessyLogo} alt="Speedyssey background" className="w-full h-auto" />
      </div>
    </footer>
  );
};

export default Footer;